import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import properties from '../../core-utils/properties';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Loader from '../../_components/molecules/Loader';
import useSnackbar from '../../core-utils/custom-hooks/Snackbar';
import Snackbar from '../../_components/atoms/Snackbar';
import {
  Breadcrumbs,
  OverviewCard,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { CARE_NAVIGATOR } from '../../core-utils/routes';
import TabSelection from '../../_components/molecules/TabSelection';
import CareNavigatorProfileCard from '../../_components/organisms/CareNavigatorProfileCard';
import {
  formatCareNavigatorCardData,
  formatCareNavigatorModalData,
  formatCareNavigatorProfileDetails,
} from '../../core-utils/helper-utils/careNavigatorHelper';
import CareNavigatorConstants from '../../core-utils/messages/CareNavigatorConstants';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import CreateCareNavModal from '../../_components/organisms/CreateCareNavModal';
import { toJS } from 'mobx';
import { useStores } from '../../stores';

interface CareNavigatorProfileProps {}

const CareNavigatorProfile: React.FC<CareNavigatorProfileProps> = () => {
  const { classes } = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: '40px 0px 66px 63px',
      width: '1182px',
      gap: '1rem',
    },
    titleContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
    },
    topCards: {
      display: 'flex',
      gap: '1rem',
    },
  })();

  const overviewCardRootStyles = {
    height: '100px',
    width: '265px',
  };

  const navigate = useNavigate();
  const location = useLocation();
  const state: any = location.state;
  const care_navigator_id = state.care_navigator_id;

  const userDetailsStore = useStores().userDetailsStore;

  const pathDetails: any[] = [
    [
      'text',
      'Care Navigator',
      {
        color: patientAccessTheme.palette.primary[500],
        fontWeight: 500,
      },
      () => {
        navigate(CARE_NAVIGATOR);
      },
    ],
    [
      'text',
      'Profile',
      {
        color: patientAccessTheme.palette.text.highemphasis,
        fontWeight: 600,
      },
    ],
  ];

  const messages = CareNavigatorConstants.CARE_NAVIGATOR_PAGE.PROFILE_PAGE;
  const { snackbarState, handleCloseSnackBar, openSnackbar } = useSnackbar();

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [careNavigatorDetails, setCareNavigatorDetails] = useState<any>({});

  const [isAddCareNav, setIsAddCareNav] = useState<boolean>(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [assignData, setAssignData] = useState<any>({
    firstName: '',
    lastName: '',
    role: '',
    email: '',
    contact: '',
    experience: '',
    speciality: '',
    groups: [],
    features: [],
  });

  const TopCards = () => {
    return (
      <Grid className={classes.topCards}>
        <OverviewCard
          title={messages.TOP_CARDS.CASES_OPENED}
          value={
            <Typography
              fontSize="20px"
              lineHeight="43px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {careNavigatorDetails.activeCases}
            </Typography>
          }
          rootStyles={overviewCardRootStyles}
          showClickHere
        />
        <OverviewCard
          title={messages.TOP_CARDS.CASES_CLOSED}
          value={
            <Typography
              fontSize="20px"
              lineHeight="43px"
              fontType="medium"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {careNavigatorDetails.closedCases}
            </Typography>
          }
          rootStyles={overviewCardRootStyles}
          showClickHere
        />
      </Grid>
    );
  };

  const getCareNavigatorDetails = async () => {
    const url =
      properties.aiqBaseUrl +
      properties.careNavigatorBaseURL +
      properties.getCareNavigatorProfile;

    const requestData = {
      cn_id: care_navigator_id,
    };
    return await axios.post(url, requestData);
  };
  const getCareNavigatorGroups = async () => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.groupsBaseURL +
        properties.getCareNavigatorGroups,
    );
  };
  const updateCareNavigator = async (email: string) => {
    const features = assignData.features?.filter(
      (item: any) => item.value !== 'CARE_NAVIGATION',
    );
    return await axios.patch(
      properties.userManagementBaseUrl +
        properties.careNavigatorBaseURL +
        `/${email}`,
      {
        ...assignData,
        features: [
          'CARE_NAVIGATION',
          ...features?.map((item: any) => item.value),
        ],
        groups: assignData.groups.map((group: any) => ({
          groupId: group.id,
          groupName: group.name,
        })),
      },
    );
  };
  const addCareNavigatorToGroups = async (
    groups: any,
    email: string,
    name: string,
    role: string,
  ) => {
    return await axios.post(
      properties.aiqBaseUrl +
        properties.groupsBaseURL +
        properties.addCareNavigatorToGroups,
      {
        groups: groups,
        email: email,
        name: name,
        role: role,
      },
    );
  };

  const handleCareNavigatorUpdate = async () => {
    setIsAddCareNav(false);
    await updateCareNavigator(assignData?.email).then((response) => {
      if (response.status === 200) {
        addCareNavigatorToGroups(
          assignData.groups,
          assignData.email,
          assignData.firstName + ' ' + assignData.lastName,
          assignData.role,
        );
        openSnackbar('Care Navigator updated succesfully', 'success', 6000);
        apiCalls();
      }
    });
  };

  const apiCalls = async () => {
    setLoading(true);
    await Promise.allSettled([
      getCareNavigatorDetails(),
      getCareNavigatorGroups(),
    ]).then((responses) => {
      setLoading(false);
      if (responses[0].status === 'rejected') {
        openSnackbar('Care Navigator Fetch Error', 'error', 5000);
      }
      if (responses[0].status === 'fulfilled') {
        setCareNavigatorDetails(responses[0].value.data?.data);
        setAssignData(
          formatCareNavigatorModalData(
            responses[0].value.data?.data,
            toJS(userDetailsStore.allScreens)?.careNavigation,
          ),
        );
      }
      if (responses[1].status === 'rejected') {
        openSnackbar('Care Navigator Fetch Error', 'error', 5000);
      }
      if (responses[1].status === 'fulfilled') {
        setGroups(responses[1].value.data?.data);
      }
    });
    setLoading(false);
  };
  useEffect(() => {
    apiCalls();
  }, []);

  if (loading) {
    return <Loader />;
  }
  return (
    <>
      <Grid container className={classes.root}>
        <Grid className={classes.titleContainer}>
          <Breadcrumbs itemList={pathDetails} />
          <PageTitleAndSubTitle
            title={
              CareNavigatorConstants.CARE_NAVIGATOR_PAGE.CARE_NAVIGATORS_DATA
                .title
            }
            subTitle={
              CareNavigatorConstants.CARE_NAVIGATOR_PAGE.CARE_NAVIGATORS_DATA
                .subTitle
            }
          />
        </Grid>
        <Grid>
          <TopCards />
        </Grid>
        <Grid>
          <TabSelection
            selectedTabIndex={selectedTabIndex}
            setSelectedTabIndex={setSelectedTabIndex}
            tabList={['Profile']}
            margin=""
          />
          {selectedTabIndex === 0 && (
            <CareNavigatorProfileCard
              {...formatCareNavigatorCardData(careNavigatorDetails)}
              details={formatCareNavigatorProfileDetails(careNavigatorDetails)}
              setOpen={setIsAddCareNav}
            />
          )}
        </Grid>
      </Grid>
      <CreateCareNavModal
        open={isAddCareNav}
        handleClose={() => {
          setIsAddCareNav(false);
        }}
        handleSave={handleCareNavigatorUpdate}
        isSaveButtonDisabled={false}
        assignCareNavState={[assignData, setAssignData]}
        groups={groups}
        isEditModal={true}
      />
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
    </>
  );
};

export default CareNavigatorProfile;
